import { useEffect, useRef, useState } from "react";
import { api, apiError, inr } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Save, Upload, Download, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export function SupplierEmailCell({ clientId, s, canEdit, onSaved }) {
  const [email, setEmail] = useState(s.email || "");
  const id = s.counterparty_gstin;
  const save = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error("Enter a valid email address"); return; }
    try { await api.put(`/clients/${clientId}/suppliers/${id}/contact`, { email }); toast.success("Email saved"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!canEdit) return <span>{s.email || "—"}</span>;
  return (
    <div className="flex items-center gap-1">
      <input data-testid={`supplier-email-input-${id}`} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="supplier@email"
        className="w-44 rounded border border-border bg-background px-2 py-1 text-[11px] font-mono" />
      {email !== (s.email || "") && <button data-testid={`supplier-email-save-${id}`} onClick={save} className="rounded p-1 hover:bg-muted" title="Save email"><Save className="h-3.5 w-3.5" /></button>}
    </div>
  );
}

async function downloadBlob(url, fallback) {
  const res = await api.get(url, { responseType: "blob" });
  const m = (res.headers["content-disposition"] || "").match(/filename\*=UTF-8''([^;]+)/);
  const a = document.createElement("a");
  a.href = URL.createObjectURL(res.data); a.download = m ? decodeURIComponent(m[1]) : fallback; a.click(); URL.revokeObjectURL(a.href);
}

export function SupplierEmailUpload({ clientId, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const template = async () => {
    try { await downloadBlob(`/clients/${clientId}/supplier-contacts/template`, "Supplier Emails.xlsx"); } catch (e) { toast.error(apiError(e)); }
  };
  const upload = async (f) => {
    if (!f) { return; }
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/clients/${clientId}/supplier-contacts/import`, fd);
      const msg = `${data.saved} email(s) saved${data.error_count ? `, ${data.error_count} row(s) with errors` : ""}`;
      if (data.error_count) { toast.warning(msg, { description: data.errors.slice(0, 5).map((e) => `Row ${e.row}: ${e.error}`).join(" · ") }); } else { toast.success(msg); }
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
    ref.current.value = "";
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button data-testid="supplier-email-template-button" onClick={template} className="flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] hover:bg-muted"><Download className="h-3 w-3" /> Email sheet</button>
      <button data-testid="supplier-email-upload-button" disabled={busy} onClick={() => ref.current.click()} className="flex items-center gap-1 rounded border border-border px-2 py-1 text-[11px] hover:bg-muted disabled:opacity-50"><Upload className="h-3 w-3" /> {busy ? "Uploading…" : "Upload emails"}</button>
      <input ref={ref} data-testid="supplier-email-upload-input" type="file" accept=".xlsx" className="hidden" onChange={(e) => upload(e.target.files[0])} />
    </div>
  );
}

export function HistoryButton({ gstin, onOpen }) {
  return <button data-testid={`supplier-history-button-${gstin}`} onClick={() => onOpen(gstin)} className="mt-0.5 flex items-center gap-1 text-[10px] text-indigo-600 hover:underline"><History className="h-3 w-3" /> History</button>;
}

export function SupplierHistoryDialog({ clientId, gstin, onClose }) {
  const [h, setH] = useState(null);
  useEffect(() => {
    if (!gstin) { return; }
    setH(null);
    api.get(`/clients/${clientId}/suppliers/${gstin}/reminders`).then((r) => setH(r.data.reminders)).catch((e) => toast.error(apiError(e)));
  }, [clientId, gstin]);
  const fmt = (t) => new Date(t).toLocaleDateString("en-GB").replaceAll("/", "-");
  return (
    <Dialog open={!!gstin} onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-w-xl w-[calc(100%-1.5rem)] max-h-[85vh] overflow-y-auto" data-testid="supplier-history-dialog">
        <DialogHeader><DialogTitle>Reminder history</DialogTitle><DialogDescription className="font-mono">{gstin}</DialogDescription></DialogHeader>
        {h && h.length === 0 && <p className="text-sm text-muted-foreground">No reminders sent yet.</p>}
        {h && h.map((r) => (
          <div key={r.id} className="rounded-md border border-border p-3 text-xs space-y-1.5" data-testid={`history-entry-${r.id}`}>
            <div className="flex flex-wrap justify-between gap-2"><span>{fmt(r.sent_at)} · {r.sent_by} → {r.to}</span>
              <span className={r.resolved === r.invoice_status.length ? "text-emerald-600" : "text-amber-600"}>{r.resolved}/{r.invoice_status.length} now in GSTR-2B</span></div>
            <div className="text-muted-foreground">FY {r.fy} · GST {inr(r.tax_at_risk)}</div>
            <div className="flex flex-wrap gap-1.5">{r.invoice_status.map((i) => (
              <span key={i.invoice_no} className={`rounded px-1.5 py-0.5 font-mono ${i.twob_period ? "bg-emerald-500/10 text-emerald-700" : "bg-amber-500/10 text-amber-700"}`}>
                {i.invoice_no} {i.twob_period ? `· in 2B ${i.twob_period}` : "· still missing"}</span>))}</div>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}

export function SupplierRemindDialog({ clientId, fy, gstin, onClose, onSent }) {
  const [p, setP] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!gstin) { return; }
    setP(null);
    api.get(`/clients/${clientId}/suppliers/${gstin}/reminder-preview?fy=${fy}`)
      .then((r) => { setP(r.data); setNote(r.data.note); })
      .catch((e) => { toast.error(apiError(e)); onClose(); });
  }, [clientId, fy, gstin, onClose]);
  const send = async () => {
    setBusy(true);
    try { await api.post(`/clients/${clientId}/suppliers/${gstin}/remind`, { fy, note }); toast.success(`Email sent to ${p.to}`); onSent(); onClose(); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!gstin} onOpenChange={(o) => { if (!o) { onClose(); } }}>
      <DialogContent className="max-w-xl" data-testid="supplier-remind-dialog">
        <DialogHeader>
          <DialogTitle>Request GSTR-1 filing</DialogTitle>
          <DialogDescription>Replies come to you at {p?.reply_to}.</DialogDescription>
        </DialogHeader>
        {p && <div className="space-y-3 text-sm">
          <div className="text-xs font-mono space-y-0.5">
            <div>To: <span data-testid="remind-to">{p.to}</span></div>
            <div>Subject: {p.subject}</div>
            {p.last_reminded_at && <div className="text-amber-600">Last emailed {new Date(p.last_reminded_at).toLocaleDateString("en-GB").replaceAll("/", "-")}</div>}
          </div>
          <Textarea data-testid="remind-note-textarea" value={note} onChange={(e) => setNote(e.target.value)} rows={7} maxLength={1500} className="text-sm" />
          <div className="text-xs text-muted-foreground">Followed by the invoice table ({p.invoices.length} invoice(s), GST {inr(p.tax_at_risk)}) and your name.</div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="rounded-md border border-border px-3 py-2 text-xs" data-testid="remind-cancel-button">Cancel</button>
            <button onClick={send} disabled={busy || note.trim().length < 20} data-testid="remind-send-button"
              className="flex items-center gap-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white px-3 py-2 text-xs font-medium">
              <Mail className="h-3.5 w-3.5" /> {busy ? "Sending…" : "Send email"}
            </button>
          </div>
        </div>}
      </DialogContent>
    </Dialog>
  );
}
